import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { CalculatedValue } from "../../models/text/CalculatedValue";
import { ISplitOptionItem } from "../../models/text/ISplitOptionItem";
import { ISplitOptions } from "../../models/text/ISplitOptions";
import { StringObject } from "../../objects/StringObject";
import { BuildDOMTree } from "../../utils/text/BuildDOMTree";
import { BuildTokens } from "../../utils/text/BuildTokens";
import { LayoutMeasurer } from "../../utils/text/LayoutMeasurer";
import {
  LayoutLine,
  ProcessedChar,
  ProcessedWord,
  SplitMeasuredTokens,
} from "../../utils/text/SplitMeasuredTokens";

/**
 * StringSplit module: splits text into lines, words, chars,
 * computes alignment/random values, and applies CSS vars.
 */
export class StringSplit extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "split";
    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    //this.permissions.mobile.rebuild.scrollHeight = false;
  }

  onResizeWidth(): void {
    this.objectsOnPage.forEach((object) => {
      this.onObjectConnected(object);
    });
  }

  onObjectConnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!element) return;

    const isAlreadySplit = element.classList.contains("-splitted");
    let originalHtml = element.getAttribute("string-split-original");

    if (!isAlreadySplit || originalHtml === null) {
      originalHtml = this.escapeAttribute(element.innerHTML);
      element.setAttribute("string-split-original", originalHtml);
      element.classList.add("-splitted");
    }

    object.htmlElement.innerHTML = originalHtml;
    const attr =
      element.getAttribute("string-split") ?? element.getAttribute("data-string-split") ?? "";
    const options: ISplitOptions = this.tools.optionsParser.process({
      attributeValue: attr,
    });
    const { fragment, result } = this.split(element, options);
    object.setProperty("nodes", fragment.childNodes);
    element.setAttribute("aria-label", originalHtml);
    element.innerHTML = "";
    element.appendChild(result);

    const restoreAfter = element.getAttribute("string-split-restore-after");
    if (restoreAfter && !isNaN(Number(restoreAfter))) {
      setTimeout(() => {
        element.innerHTML = originalHtml!;
        element.classList.add("-restored");
      }, Number(restoreAfter));
    }
  }

  public split(element: HTMLElement, options: ISplitOptions) {
    const frag = document.createDocumentFragment();
    element.childNodes.forEach((ch) => frag.appendChild(ch.cloneNode(true)));
    const tokens = BuildTokens(frag.childNodes as NodeListOf<ChildNode>);
    const measured = LayoutMeasurer(tokens, element);
    const layoutLines: LayoutLine[] = SplitMeasuredTokens(measured);
    this.applyCalculatedValues(layoutLines, options);
    const dom = BuildDOMTree(layoutLines, options);
    return {
      fragment: frag,
      result: dom,
    };
  }

  /**
   * Computes a numeric value based on the provided split option, index, and total count.
   *
   * @param opt - The split option item containing alignment and optional random range.
   * @param index - The current index in the sequence.
   * @param total - The total number of items in the sequence.
   * @returns A computed numeric value based on the alignment strategy:
   *          - "random": A random value within the specified range or default range.
   *          - "start": The current index.
   *          - "end": The reverse index from the end of the sequence.
   *          - "center": The distance from the center index.
   *          - Default: The current index.
   */
  private computeValue(opt: ISplitOptionItem, index: number, total: number): number {
    if (opt.align.startsWith("random")) {
      const min = opt.random?.min ?? 0;
      const max = opt.random?.max ?? total - 1;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }
    switch (opt.align) {
      case "start":
        return index;
      case "end":
        return total - index - 1;
      case "center": {
        const centerIdx = Math.floor((total - 1) / 2);
        return Math.abs(index - centerIdx);
      }
      default:
        return index;
    }
  }

  /**
   * Applies calculated values to the provided layout lines and their components (words and characters)
   * based on the given options. This method computes and assigns various calculated values
   * (e.g., alignment and value) to lines, words, and characters, depending on the specified options.
   *
   * @param lines - An array of `LayoutLine` objects representing the lines of text to process.
   * @param options - An `ISplitOptions` object specifying the calculation options for lines, words, and characters.
   *
   * The `options` parameter can include the following properties:
   * - `line`: An array of options for calculating values at the line level.
   * - `word`: An array of options for calculating values at the word level.
   * - `wordLine`: An array of options for calculating values at the word level relative to the line.
   * - `char`: An array of options for calculating values at the character level.
   * - `charWord`: An array of options for calculating values at the character level relative to the word.
   * - `charLine`: An array of options for calculating values at the character level relative to the line.
   *
   * Each calculated value is assigned to the corresponding `calculatedValues` property of the line, word, or character.
   * The method ensures that all specified options are processed and the calculated values are appended accordingly.
   */
  private applyCalculatedValues(lines: LayoutLine[], options: ISplitOptions): void {
    const countChars = (ln: LayoutLine) =>
      ln.words.reduce((acc, w: ProcessedWord) => acc + w.chars.length, 0);

    const totalWords = lines.reduce((acc, l) => acc + l.words.length, 0);
    const totalCharsGlobal = lines.reduce(
      (acc, l) => acc + l.words.reduce((acc2, w) => acc2 + w.chars.length, 0),
      0
    );

    lines.forEach((line, li) => {
      if (options.line) {
        (line as any).calculatedValues = options.line.map(
          (opt) =>
            ({
              type: "line",
              align: opt.align,
              value: this.computeValue(opt, li, lines.length),
            } as CalculatedValue)
        );
      }

      line.words.forEach((word: ProcessedWord, wi) => {
        if (options.word) {
          (word as any).calculatedValues = options.word.map(
            (opt) =>
              ({
                type: "word",
                align: opt.align,
                value: this.computeValue(opt, word.wordIndexGlobal, totalWords),
              } as CalculatedValue)
          );
        }

        if (options.wordLine) {
          (word as any).calculatedValues ??= [];
          (word as any).calculatedValues.push(
            ...options.wordLine.map(
              (opt) =>
                ({
                  type: "wordLine",
                  align: opt.align,
                  value: this.computeValue(opt, word.wordIndexInLine, line.words.length),
                } as CalculatedValue)
            )
          );
        }

        const totalCharsInLine = countChars(line);

        word.chars.forEach((char: ProcessedChar) => {
          const cvs: CalculatedValue[] = [];

          if (options.char) {
            cvs.push(
              ...options.char.map(
                (opt) =>
                  ({
                    type: "char",
                    align: opt.align,
                    value: this.computeValue(opt, char.charIndexGlobal, totalCharsGlobal),
                  } as CalculatedValue)
              )
            );
          }

          if (options.charWord) {
            cvs.push(
              ...options.charWord.map(
                (opt) =>
                  ({
                    type: "charWord",
                    align: opt.align,
                    value: this.computeValue(opt, char.charIndexInWord, word.chars.length),
                  } as CalculatedValue)
              )
            );
          }

          if (options.charLine) {
            cvs.push(
              ...options.charLine.map(
                (opt) =>
                  ({
                    type: "charLine",
                    align: opt.align,
                    value: this.computeValue(opt, char.charIndexInLine, totalCharsInLine),
                  } as CalculatedValue)
              )
            );
          }

          (char as any).calculatedValues = cvs;
        });
      });
    });
  }

  /**
   * Escapes the `src` attribute in a given string by removing the quotes around the URL.
   *
   * This method searches for occurrences of `src="URL"` where `URL` is an HTTP or HTTPS link,
   * and replaces it with `src=URL` (removing the quotes around the URL).
   *
   * @param str - The input string containing the `src` attributes to be escaped.
   * @returns The modified string with escaped `src` attributes.
   */
  private escapeAttribute(str: string): string {
    return str.replace(/src="(https?:\/\/[^"\s]+)"/g, "src=$1");
  }
}
